import DOMPurify from "dompurify";
import type { UponSanitizeElementHook } from "dompurify";

export interface SanitizeOptions { allowRemoteImages: boolean; }

/**
 * True when a URL points off-origin: absolute http(s) OR protocol-relative
 * (`//host/...`). Leading whitespace is tolerated (an evasion vector). Relative
 * paths, `data:`, and `cid:` URLs are treated as local and stay allowed.
 */
function isRemoteUrl(u: string): boolean {
  return /^\s*(https?:)?\/\//i.test(u);
}

/** True when a comma-separated srcset has ANY remote candidate URL. */
function srcsetHasRemote(srcset: string): boolean {
  return srcset
    .split(",")
    .map((candidate) => candidate.trim().split(/\s+/)[0] ?? "")
    .some((url) => url !== "" && isRemoteUrl(url));
}

/** True when an inline style declares a `url(...)` pointing at a remote target. */
function styleHasRemoteUrl(style: string): boolean {
  const re = /url\(\s*(['"]?)([^'")]+)\1\s*\)/gi;
  let m: RegExpExecArray | null;
  while ((m = re.exec(style)) !== null) {
    if (isRemoteUrl(m[2] ?? "")) return true;
  }
  return false;
}

// Elements whose media attributes can trigger a remote fetch (tracker pixels,
// beacons via <video poster>/<source>, etc.).
const REMOTE_MEDIA_TAGS = new Set(["IMG", "IMAGE", "VIDEO", "AUDIO", "SOURCE"]);

/**
 * Sanitises inbound/outbound email HTML for display. Always removes scripts/event handlers.
 * When allowRemoteImages is false, remote-content vectors are stripped to block tracker
 * pixels/beacons: <img>/<image>/<video>/<audio>/<source> whose src/srcset/poster reference a
 * remote URL (absolute http(s) OR protocol-relative //host) are removed, and any element whose
 * inline style carries a remote `url(...)` background has its style attribute dropped. A
 * per-message "Show external content" toggle re-runs with allowRemoteImages: true.
 */
export function sanitizeEmailHtml(html: string, { allowRemoteImages }: SanitizeOptions): string {
  const hook: UponSanitizeElementHook = (node) => {
    if (!(node instanceof Element)) return;

    // Drop media elements that reference remote content via src/srcset/poster.
    if (REMOTE_MEDIA_TAGS.has(node.tagName)) {
      const src = node.getAttribute("src") ?? "";
      const srcset = node.getAttribute("srcset") ?? "";
      const poster = node.getAttribute("poster") ?? "";
      if (isRemoteUrl(src) || isRemoteUrl(poster) || (srcset !== "" && srcsetHasRemote(srcset))) {
        node.parentNode?.removeChild(node);
        return;
      }
    }

    // Strip remote CSS background urls: drop the whole inline style (simplest safe approach).
    const style = node.getAttribute("style");
    if (style && styleHasRemoteUrl(style)) node.removeAttribute("style");
  };
  if (!allowRemoteImages) DOMPurify.addHook("uponSanitizeElement", hook);
  try {
    return DOMPurify.sanitize(html, { USE_PROFILES: { html: true } });
  } finally {
    if (!allowRemoteImages) DOMPurify.removeHook("uponSanitizeElement");
  }
}
